import { useCallback, useEffect, useRef, useState } from "react";
import { ISSOUser } from "../../services/typings";
import { useHistory, useParams } from "react-router";
import contractServices from "pages/Contract/services/contractServices";
import userService from "components/CurrentUser/services/userService";
import { UserType } from "components/CurrentUser/constants";
import { find, includes } from "lodash";
import { ContractStatus } from "pages/Contract/constants";
import { Permission, Resource } from "components/CurrentUser/constants";
import { useSSO } from "pages/Auth/hooks";
import { useCurrentUser } from "components/CurrentUser/hooks";
import registerService from "../Register/services/registerService";
import { useTranslation } from "react-i18next";
import { mutate } from "swr";
import RestAPIClient from "services/RestAPIClient";
import Cookies from "js-cookie";
import { extractJwtPayload } from "utils/auth";

export const useLogin = () => {
  const [loading, setLoading] = useState(false);

  const mounted = useRef(true);

  const tokenRef = useRef("");

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const { replace, push } = useHistory();

  const checkAuthenContractUser = useCallback((role: any) => {
    return (
      role.permissions[`${Resource.USER_CONTRACT}:${Permission.VIEW}`] || false
    );
  }, []);

  const { login: loginWithSSO, getAccessToken } = useSSO();

  const [_, __, ___, mutateUser] = useCurrentUser({ revalidateOnMount: false });

  const { t } = useTranslation();

  const login = useCallback(async () => {
    try {
      setLoading(true);
      const res = await loginWithSSO();
      const { has_account, login_err } = await registerService.isWhitelist({
        token: res?.accessToken,
      });
      const urlParams = new URLSearchParams(window.location.search);

      const userType = urlParams.get("type") || "kol";
      if (!has_account) {
        mutate("AUTH_USERS");
        push({
          pathname: "/register",
          state: { token: res?.accessToken },
          search: `?type=${userType}`,
        });
        return;
      }
      if (login_err) {
        mutate("AUTH_USERS");
        throw new Error(login_err);
      }
      const customerId = extractJwtPayload(res.accessToken)?.customer_id;
      const user = await userService.getCurrentUser({ token: res.accessToken });
      mutateUser(user, false);
      const { type: user_type, role } = user || {};
      if (
        includes(
          [UserType.AFFILIATOR, UserType.PARTNER, UserType.OPERATOR],
          user_type
        )
      ) {
        RestAPIClient.token = res.accessToken;
        Cookies.set("customer_id", `${customerId}`);
        replace(urlParams.get("redirect") || "/");
        return res;
      }

      const isAuthenCcntractUser = checkAuthenContractUser(role);
      if (isAuthenCcntractUser) {
        const contractMe = await contractServices.getContractMe({
          token: res.accessToken,
        });
        RestAPIClient.token = res.accessToken;
        Cookies.set("customer_id", `${customerId}`);
        const { status: statusContract } = contractMe || {};
        if (
          !includes(
            [ContractStatus.APPROVE, ContractStatus.CONFIRM],
            statusContract
          )
        ) {
          replace("/account/contract");
        } else {
          replace(urlParams.get("redirect") || "/");
        }
      } else {
        RestAPIClient.token = res.accessToken;
        Cookies.set("customer_id", `${customerId}`);
        replace(urlParams.get("redirect") || "/");
      }

      return res;
    } catch (e) {
      mounted.current && setLoading(false);
      throw e;
    } finally {
      mounted.current && setLoading(false);
    }
  }, [checkAuthenContractUser, loginWithSSO, mutateUser, push, replace]);

  const loginWithExistUser = useCallback(
    async (ssoUser: ISSOUser, loggedOut?: boolean) => {
      try {
        setLoading(true);
        const res = await getAccessToken(ssoUser.accountIndex);
        const { has_account, login_err } = await registerService.isWhitelist({
          token: res.accessToken,
        });
        const urlParams = new URLSearchParams(window.location.search);
        const userType = urlParams.get("type") || "kol";
        if (!has_account) {
          push({
            pathname: "/register",
            state: { token: res?.accessToken },
            search: `?type=${userType}`,
          });
          return;
        }
        if (!!login_err) throw new Error(login_err);
        tokenRef.current = res.accessToken;
        const user = await userService.getCurrentUser({
          token: res.accessToken,
        });
        mutateUser(user, false);
        const { type: user_type, role } = user || {};

        if (
          includes(
            [UserType.AFFILIATOR, UserType.PARTNER, UserType.OPERATOR],
            user_type
          )
        ) {
          RestAPIClient.token = res.accessToken;
          Cookies.set("customer_id", `${ssoUser?.customerId}`);
          replace(urlParams.get("redirect") || "/");
          return res;
        }

        const isAuthenCcntractUser = checkAuthenContractUser(role);
        if (isAuthenCcntractUser) {
          const contractMe = await contractServices.getContractMe({
            token: res.accessToken,
          });
          RestAPIClient.token = res.accessToken;
          Cookies.set("customer_id", `${ssoUser?.customerId}`);
          const { status: statusContract } = contractMe || {};
          if (
            !includes(
              [ContractStatus.APPROVE, ContractStatus.CONFIRM],
              statusContract
            )
          ) {
            replace("/account/contract");
          } else {
            replace(urlParams.get("redirect") || "/");
          }
        } else {
          RestAPIClient.token = res.accessToken;
          Cookies.set("customer_id", `${ssoUser?.customerId}`);
          replace(urlParams.get("redirect") || "/");
        }

        return res;
      } catch (e) {
        mounted.current && setLoading(false);
        throw e;
      } finally {
        mounted.current && setLoading(false);
      }
    },
    [getAccessToken, mutateUser, checkAuthenContractUser, push, replace]
  );

  return { login, loginWithExistUser, loading };
};
